<template>
  <div
    class="procedure-report-page-scroll infinite-scroll-table procedure-report-page-scroll--sticky-header"
    :class="{
      'infinite-scroll-table--resizable': enableColumnResize,
      'procedure-report-page-scroll--header-stuck': headerStuck,
    }">
    <v-data-table
      ref="dataTable"
      class="procedure-report-page-scroll__data-table"
      :style="dataTableStyle"
      v-bind="$attrs"
      v-on="listenersForDataTable"
      :items="items"
      :loading="loading"
      :sort-by="localSortBy"
      :sort-desc="localSortDesc"
      hide-default-footer
      hide-default-header
      disable-pagination
      dense
      mobile-breakpoint="0">
      <template v-slot:header="{ props, on }">
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="(header, index) in props.headers"
              :key="getHeaderKey(header, index)"
              :class="[header.class, { 'infinite-scroll-header-cell--resizable': isColumnResizable(header) }]"
              :style="enableColumnResize ? getHeaderStyle(header, index) : header.style"
              :aria-sort="isSelectionHeader(header) ? null : getAriaSort(header)"
              :tabindex="header.sortable !== false && !isSelectionHeader(header) ? 0 : -1"
              :data-header-key="getHeaderKey(header, index)"
              @click="toggleSort(header)"
              @keyup.enter="toggleSort(header)"
              @keyup.space.prevent="toggleSort(header)">
              <div
                v-if="isSelectionHeader(header) && !props.singleSelect"
                class="procedure-report-select-cell">
                <v-simple-checkbox
                  :value="props.everyItem"
                  :indeterminate="!props.everyItem && props.someItems"
                  :color="props.checkboxColor || ''"
                  @click.stop
                  @input="toggleSelectAll(on, $event)"
                />
              </div>
              <div
                v-else
                class="d-flex align-center infinite-scroll-header-cell"
                :style="header.sortable !== false ? 'cursor: pointer;' : ''">
                <v-tooltip
                  bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs" class="infinite-scroll-header-text">
                      {{ header.text }}
                    </span>
                  </template>
                  <span>
                    {{ header.text }}
                  </span>
                </v-tooltip>
                <v-icon
                  v-if="header.sortable !== false && getAriaSort(header) !== 'none'"
                  small
                  class="ml-2">
                  {{ getSortIcon(header) }}
                </v-icon>
              </div>
              <div
                v-if="isColumnResizable(header)"
                class="infinite-scroll-header-resizer"
                @mousedown.stop.prevent="startResize(header, index, $event)"
                @click.stop.prevent />
            </th>
          </tr>
        </thead>
      </template>
      <template
        v-for="slotName in passThroughSlots"
        v-slot:[slotName]="slotScope">
        <slot
          :name="slotName"
          v-bind="slotScope" />
      </template>

      <template v-slot:body.append="slotScope">
        <slot
          name="body.append"
          v-bind="slotScope" />
        <tr
          class="infinite-scroll-table__sentinel-row"
          v-show="hasMore">
          <td :colspan="getColspan(slotScope)">
            <div
              ref="sentinel"
              class="infinite-scroll-table__sentinel-anchor" />
          </td>
        </tr>
      </template>
    </v-data-table>

    <div
      v-if="hasMore && loading"
      class="procedure-report-page-scroll__append-loading"
      :style="appendLoadingStyle">
      <v-progress-circular
        indeterminate
        color="primary"
        size="24" />
    </div>

    <div
      v-show="horizontalScroll.visible"
      ref="horizontalScrollMirror"
      class="procedure-report-page-scroll__hscroll-fixed"
      :style="horizontalScrollFixedStyle"
      @scroll="syncHorizontalScroll('mirror')">
      <div
        class="procedure-report-page-scroll__hscroll-spacer"
        :style="{ width: `${horizontalScroll.contentWidth}px` }" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProcedureReportPageScroll',
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: true,
    },
    autoLoadOnMount: {
      type: Boolean,
      default: true,
    },
    threshold: {
      type: Number,
      default: 0.25,
    },
    rootMargin: {
      type: String,
      default: '0px 0px 200px 0px',
    },
    triggerPercent: {
      type: Number,
      default: 0.9,
    },
    sortBy: {
      type: Array,
      default: () => [],
    },
    sortDesc: {
      type: Array,
      default: () => [],
    },
    enableColumnResize: {
      type: Boolean,
      default: false,
    },
    fitResizableColumnsToWrapper: {
      type: Boolean,
      default: false,
    },
    resizableColumns: {
      type: Array,
      default: () => [],
    },
    minColumnWidth: {
      type: Number,
      default: 50,
    },
  },
  data: () => ({
    observer: null,
    localSortBy: [],
    localSortDesc: [],
    scrollContainer: null,
    columnWidths: {},
    resizeState: null,
    hasUserResizedColumns: false,
    loadStartItemsLength: 0,
    loadMoreRequestPending: false,
    loadingStartedFromLoadMore: false,
    loadMoreSuspended: false,
    horizontalScroll: {
      visible: false,
      contentWidth: 0,
      left: 0,
      width: 0,
      syncing: false,
    },
    appendLoadingTop: 0,
    headerStuck: false,
    headerFixed: false,
    stickyLayoutEl: null,
    stickyHeaderCache: null,
    stickyHeaderColumnsSynced: false,
    stickyHeaderViewportWidth: 0,
    stickyHeaderStickScrollY: null,
    stickyHeaderHeight: 0,
    stickyHeaderSyncRaf: null,
    touchScroll: {
      active: false,
      axis: null,
      startX: 0,
      startY: 0,
      startScrollLeft: 0,
    },
    isComponentVisible: true,
    visibilityObserver: null,
    horizontalScrollResizeObserver: null,
  }),
  computed: {
    listenersForDataTable() {
      const listeners = { ...this.$listeners };
      delete listeners['update:options'];
      return listeners;
    },
    dataTableStyle() {
      return null;
    },
    passThroughSlots() {
      return Object.keys(this.$scopedSlots || {}).filter(
        (slotName) => slotName !== 'body.append',
      );
    },
    normalizedHeaders() {
      const { headers } = this.$attrs;
      return Array.isArray(headers) ? headers : [];
    },
    shouldFitResizableColumnsToWrapper() {
      return this.fitResizableColumnsToWrapper
        && !this.hasUserResizedColumns
        && Array.isArray(this.items)
        && this.items.length === 0;
    },
    horizontalScrollFixedStyle() {
      return {
        left: `${this.horizontalScroll.left}px`,
        width: `${this.horizontalScroll.width}px`,
      };
    },
    appendLoadingStyle() {
      return {
        left: `${this.horizontalScroll.left}px`,
        top: `${this.appendLoadingTop}px`,
        width: `${this.horizontalScroll.width}px`,
      };
    },
  },
  watch: {
    hasMore(newValue) {
      if (!newValue) {
        this.resumeLoadMore();
        this.disconnectObserver();
      } else {
        this.resumeLoadMore();
        this.$nextTick(() => {
          this.connectObserver();
        });
      }
    },
    loading(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.loadStartItemsLength = this.getItemsLength();
        this.loadingStartedFromLoadMore = this.loadMoreRequestPending;
        this.loadMoreRequestPending = false;
        this.$nextTick(() => {
          this.updateAppendLoadingPosition();
          this.measureStickyHeaderStickPoint();
          this.scheduleStickyHeaderSync();
        });
        return;
      }

      if (oldValue && !newValue && this.hasMore) {
        const startedFromLoadMore = this.loadingStartedFromLoadMore;
        const loadedMoreItems = this.getItemsLength() > this.loadStartItemsLength;
        if (startedFromLoadMore && !loadedMoreItems) {
          this.loadMoreSuspended = true;
          this.disconnectObserver();
          this.loadingStartedFromLoadMore = false;
          return;
        }

        this.resumeLoadMore();
        this.loadingStartedFromLoadMore = false;
        this.$nextTick(() => {
          this.reconnectObserver();
          this.requestMoreIfNearEnd();
          this.measureStickyHeaderStickPoint();
          this.updateHorizontalScroll();
          this.scheduleStickyHeaderSync();
        });
      }
    },
    items(newItems = [], oldItems = []) {
      if (newItems.length !== oldItems.length) {
        this.resumeLoadMore();
      }

      this.$nextTick(() => {
        if (
          this.enableColumnResize
          && this.fitResizableColumnsToWrapper
          && !this.hasUserResizedColumns
        ) {
          this.resetColumnWidths();
          this.syncColumnWidths();
        }
        this.refreshScrollContainer();
        this.stickyHeaderColumnsSynced = false;
        this.measureStickyHeaderStickPoint();
        this.updateHorizontalScroll();
        this.updateAppendLoadingPosition();
        this.scheduleStickyHeaderSync();
      });
    },
    sortBy: {
      handler(val = []) {
        this.resumeLoadMore();
        this.localSortBy = Array.isArray(val) ? [...val] : [];
      },
      immediate: true,
    },
    sortDesc: {
      handler(val = []) {
        this.resumeLoadMore();
        this.localSortDesc = Array.isArray(val) ? [...val] : [];
      },
      immediate: true,
    },
    enableColumnResize(value) {
      if (!value) {
        this.resetColumnWidths();
        this.cleanupResizeEvents();
        return;
      }

      this.$nextTick(() => {
        this.syncColumnWidths();
      });
    },
    normalizedHeaders: {
      handler() {
        if (!this.enableColumnResize) return;
        this.$nextTick(() => {
          this.syncColumnWidths();
        });
      },
      deep: true,
    },
    resizableColumns() {
      if (!this.enableColumnResize) return;
      this.$nextTick(() => {
        this.syncColumnWidths();
      });
    },
  },
  methods: {
    isSelectionHeader(header) {
      return header && header.value === 'data-table-select';
    },
    refreshViewportHeight() {
      this.$nextTick(() => {
        this.reconnectObserver();
        this.requestMoreIfNearEnd();
      });
    },
    getHeaderKey(header, index) {
      if (header && header.value !== undefined && header.value !== null && header.value !== '') {
        return String(header.value);
      }

      return `__col_${index}`;
    },
    isColumnResizable(header) {
      if (!this.enableColumnResize) return false;
      if (this.isSelectionHeader(header)) return false;
      if (!header || header.value === undefined || header.value === null || header.value === '') return false;

      const allowed = Array.isArray(this.resizableColumns) ? this.resizableColumns : [];
      if (!allowed.length) return true;

      return allowed.includes(header.value);
    },
    parseColumnSize(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;

      if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
      }

      return null;
    },
    getColumnWidthBounds(header) {
      const headerMinWidth = this.parseColumnSize(header && header.minWidth);
      const headerMaxWidth = this.parseColumnSize(header && header.maxWidth);

      const minWidth = Math.max(this.minColumnWidth, headerMinWidth || 0);
      const maxWidth = headerMaxWidth && headerMaxWidth >= minWidth ? headerMaxWidth : null;

      return {
        minWidth,
        maxWidth,
      };
    },
    clampColumnWidth(width, header) {
      const numericWidth = this.parseColumnSize(width);
      if (!numericWidth) return numericWidth;

      const { minWidth, maxWidth } = this.getColumnWidthBounds(header);
      if (maxWidth !== null) {
        return Math.min(Math.max(numericWidth, minWidth), maxWidth);
      }

      return Math.max(numericWidth, minWidth);
    },
    getHeaderWidth(header) {
      const width = this.parseColumnSize(header && header.width);
      if (width !== null) return width;

      const { style } = header || {};
      if (style && typeof style === 'object') {
        return this.parseColumnSize(style.width);
      }

      return null;
    },
    getHeaderStyle(header, index) {
      const styles = [];
      if (header && header.style) styles.push(header.style);

      const explicitWidth = this.getHeaderWidth(header);
      if (explicitWidth !== null) {
        styles.push({
          width: `${explicitWidth}px`,
          minWidth: `${explicitWidth}px`,
        });
      }

      const explicitMinWidth = this.parseColumnSize(header && header.minWidth);
      const explicitMaxWidth = this.parseColumnSize(header && header.maxWidth);
      if (explicitMinWidth !== null || explicitMaxWidth !== null) {
        const { minWidth, maxWidth } = this.getColumnWidthBounds(header);
        styles.push({
          ...(explicitMinWidth !== null ? { minWidth: `${minWidth}px` } : {}),
          ...(explicitMaxWidth !== null && maxWidth !== null ? { maxWidth: `${maxWidth}px` } : {}),
        });
      }

      const key = this.getHeaderKey(header, index);
      const width = this.clampColumnWidth(this.columnWidths[key], header);
      if (width) {
        styles.push({
          width: `${width}px`,
          minWidth: `${width}px`,
        });
      }

      return styles.length ? styles : null;
    },
    getColspan(slotScope) {
      if (
        slotScope
        && Array.isArray(slotScope.headers)
        && slotScope.headers.length
      ) {
        return slotScope.headers.length;
      }

      const { headers } = this.$attrs;
      if (Array.isArray(headers) && headers.length) {
        return headers.length;
      }

      return 1;
    },
    getScrollContainer() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;

      if (!tableEl) return null;

      const wrapper = tableEl.querySelector('.v-data-table__wrapper');
      if (!wrapper) return null;

      return wrapper;
    },
    refreshScrollContainer() {
      const nextContainer = this.getScrollContainer();
      const containerChanged = nextContainer !== this.scrollContainer;

      if (containerChanged) {
        this.detachScrollListener();
        this.detachHorizontalScrollListener();
        this.detachTouchScrollListener();
        this.disconnectObserver();
        this.scrollContainer = nextContainer;
        this.invalidateStickyHeaderCache();
      }

      this.stickyLayoutEl = this.$el.closest('.procedure-report-table-layout') || this.$el;
      this.attachScrollListener();
      this.attachHorizontalScrollListener();
      this.attachTouchScrollListener();
      this.connectObserver();
      this.updateHorizontalScroll();
    },
    getHeaderCells() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      if (!tableEl) return [];

      return Array.from(tableEl.querySelectorAll('thead th'));
    },
    getColgroupCols() {
      const tableRef = this.$refs.dataTable;
      const tableEl = tableRef && tableRef.$el ? tableRef.$el : this.$el;
      if (!tableEl) return [];

      return Array.from(tableEl.querySelectorAll('colgroup col'));
    },
    getHeaderKeysFromDom() {
      return this.getHeaderCells().map((cell, index) => cell.dataset.headerKey || `__col_${index}`);
    },
    fitColumnWidthsToWrapper(nextWidths, headerCells) {
      if (!this.shouldFitResizableColumnsToWrapper) return nextWidths;

      const wrapper = this.scrollContainer || this.getScrollContainer();
      const wrapperWidth = wrapper ? wrapper.clientWidth : 0;
      if (!wrapperWidth) return nextWidths;

      const totalWidth = Object.keys(nextWidths)
        .reduce((total, key) => total + nextWidths[key], 0);
      const overflow = totalWidth - wrapperWidth;
      if (overflow <= 0) return nextWidths;

      const shrinkableColumns = [];
      headerCells.forEach((cell, index) => {
        const header = this.normalizedHeaders[index];
        if (!this.isColumnResizable(header)) return;

        const key = cell.dataset.headerKey || `__col_${index}`;
        const width = nextWidths[key];
        if (!width) return;

        const { minWidth } = this.getColumnWidthBounds(header);
        const shrinkCapacity = width - minWidth;
        if (shrinkCapacity > 0) {
          shrinkableColumns.push({
            key,
            header,
            width,
            shrinkCapacity,
          });
        }
      });

      const totalShrinkCapacity = shrinkableColumns
        .reduce((total, column) => total + column.shrinkCapacity, 0);
      if (totalShrinkCapacity <= 0) return nextWidths;

      const fittedWidths = { ...nextWidths };
      const shrinkTotal = Math.min(overflow, totalShrinkCapacity);
      let remainingShrink = shrinkTotal;

      shrinkableColumns.forEach((column, index) => {
        const isLastColumn = index === shrinkableColumns.length - 1;
        const proportionalShrink = shrinkTotal * (column.shrinkCapacity / totalShrinkCapacity);
        const shrink = isLastColumn ? remainingShrink : Math.min(
          column.shrinkCapacity,
          proportionalShrink,
        );

        remainingShrink = Math.max(remainingShrink - shrink, 0);
        fittedWidths[column.key] = this.clampColumnWidth(column.width - shrink, column.header);
      });

      return fittedWidths;
    },
    syncColumnWidths() {
      if (!this.enableColumnResize) return;

      const headerCells = this.getHeaderCells();
      if (!headerCells.length) return;

      const nextWidths = {};
      headerCells.forEach((cell, index) => {
        const header = this.normalizedHeaders[index];
        const key = cell.dataset.headerKey || `__col_${index}`;
        const existingWidth = this.columnWidths[key];
        const explicitWidth = this.getHeaderWidth(header);
        const measuredWidth = this.shouldFitResizableColumnsToWrapper
          ? cell.getBoundingClientRect().width
          : Math.ceil(cell.getBoundingClientRect().width);
        const nextWidth = explicitWidth !== null ? explicitWidth : measuredWidth;
        const width = this.clampColumnWidth(existingWidth || nextWidth, header);
        if (width) nextWidths[key] = width;
      });

      this.columnWidths = this.fitColumnWidthsToWrapper(nextWidths, headerCells);
      this.stickyHeaderColumnsSynced = false;
      this.applyColumnWidths();
    },
    applyColumnWidths() {
      if (this.enableColumnResize) {
        const cols = this.getColgroupCols();
        if (cols.length) {
          const headerKeys = this.getHeaderKeysFromDom();
          cols.forEach((col, index) => {
            const key = headerKeys[index];
            const width = key ? this.columnWidths[key] : null;
            if (width) {
              col.style.width = `${width}px`;
              col.style.minWidth = `${width}px`;
            } else {
              col.style.width = '';
              col.style.minWidth = '';
            }
          });
        }
      }

      this.$nextTick(() => {
        this.updateHorizontalScroll();
        if (this.headerFixed) {
          this.stickyHeaderColumnsSynced = false;
          this.syncFixedHeaderColumnWidths();
        }
      });
    },
    startResize(header, index, event) {
      if (!this.isColumnResizable(header)) return;
      if (event && event.button !== 0) return;

      const key = this.getHeaderKey(header, index);
      const headerCell = event.currentTarget
        ? (event.currentTarget.closest ? event.currentTarget.closest('th') : event.currentTarget.parentElement)
        : null;
      const rawBaseWidth = this.columnWidths[key] || (headerCell ? headerCell.getBoundingClientRect().width : 0);
      const baseWidth = this.clampColumnWidth(rawBaseWidth, header);
      const { minWidth, maxWidth } = this.getColumnWidthBounds(header);

      this.cleanupResizeEvents();
      this.resizeState = {
        key,
        startX: event.clientX,
        startWidth: baseWidth,
        minWidth,
        maxWidth,
        userSelect: document.body.style.userSelect,
        cursor: document.body.style.cursor,
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      window.addEventListener('mousemove', this.handleResizeMove);
      window.addEventListener('mouseup', this.handleResizeEnd);
    },
    handleResizeMove(event) {
      if (!this.resizeState) return;

      const delta = event.clientX - this.resizeState.startX;
      let nextWidth = Math.max(this.resizeState.startWidth + delta, this.resizeState.minWidth || this.minColumnWidth);

      if (this.resizeState.maxWidth !== null && this.resizeState.maxWidth !== undefined) {
        nextWidth = Math.min(nextWidth, this.resizeState.maxWidth);
      }

      this.hasUserResizedColumns = true;
      this.stickyHeaderColumnsSynced = false;
      this.$set(this.columnWidths, this.resizeState.key, nextWidth);
      this.applyColumnWidths();
    },
    handleResizeEnd() {
      this.cleanupResizeEvents();
      this.$nextTick(() => {
        this.updateHorizontalScroll();
        if (this.headerFixed) {
          this.stickyHeaderColumnsSynced = false;
          this.syncFixedHeaderColumnWidths();
        }
      });
    },
    cleanupResizeEvents() {
      window.removeEventListener('mousemove', this.handleResizeMove);
      window.removeEventListener('mouseup', this.handleResizeEnd);

      if (!this.resizeState) return;

      document.body.style.userSelect = this.resizeState.userSelect || '';
      document.body.style.cursor = this.resizeState.cursor || '';
      this.resizeState = null;
    },
    resetColumnWidths() {
      this.columnWidths = {};
      this.hasUserResizedColumns = false;
      const cols = this.getColgroupCols();
      cols.forEach((col) => {
        col.style.width = '';
        col.style.minWidth = '';
      });
    },
    attachScrollListener() {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize, { passive: true });
    },
    detachScrollListener() {
      window.removeEventListener('scroll', this.onWindowScroll);
      window.removeEventListener('resize', this.onWindowResize);
    },
    onWindowResize() {
      if (!this.headerFixed) {
        this.measureStickyHeaderStickPoint();
      }
      this.updateHorizontalScroll();
    },
    onWindowScroll() {
      this.requestMoreIfNearEnd();
      this.updateScrollAnchors();
    },
    getWindowScrollY() {
      return window.pageYOffset
        || document.documentElement.scrollTop
        || document.body.scrollTop
        || 0;
    },
    measureStickyHeaderStickPoint() {
      if (this.headerFixed) return;

      const layout = this.stickyLayoutEl
        || this.$el.closest('.procedure-report-table-layout')
        || this.$el;
      const elements = this.getStickyHeaderElements();
      if (!layout || !elements) return;

      const { headerRow } = elements;
      const headerRect = headerRow.getBoundingClientRect();
      const scrollY = this.getWindowScrollY();

      this.stickyHeaderStickScrollY = scrollY + headerRect.top;
      this.stickyHeaderHeight = headerRow.offsetHeight;
    },
    scheduleStickyHeaderSync() {
      if (!this.isComponentVisible) return;

      if (this.stickyHeaderSyncRaf) return;

      this.stickyHeaderSyncRaf = window.requestAnimationFrame(() => {
        this.stickyHeaderSyncRaf = null;

        if (this.headerFixed) {
          this.syncFixedHeaderColumnWidths();
          this.syncFixedHeaderHorizontalScroll();
          this.refreshFixedHeaderViewport();
          return;
        }

        this.updateScrollAnchors();
      });
    },
    refreshFixedHeaderViewport() {
      const layout = this.stickyLayoutEl
        || this.$el.closest('.procedure-report-table-layout')
        || this.$el;
      const wrapper = this.getScrollContainer();
      const elements = this.getStickyHeaderElements();
      if (!layout || !wrapper || !elements || !this.headerFixed) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const { thead, headerRow } = elements;
      const scrollLeft = wrapper.scrollLeft;

      thead.style.left = `${wrapperRect.left}px`;
      thead.style.width = `${wrapperRect.width}px`;
      headerRow.style.width = `${wrapper.scrollWidth}px`;
      headerRow.style.minWidth = `${wrapper.scrollWidth}px`;
      headerRow.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;
    },
    getStickyHeaderReferenceRow(wrapper) {
      if (!wrapper) return null;

      return wrapper.querySelector(
        'tbody tr:not(.procedure-report-page-scroll__header-spacer):not(.infinite-scroll-table__sentinel-row):not(.v-data-table__empty-wrapper)',
      );
    },
    ensureStickyHeaderSpacer(tbody, headerHeight, colspan) {
      let spacer = tbody.querySelector('tr.procedure-report-page-scroll__header-spacer');
      if (!spacer) {
        spacer = document.createElement('tr');
        spacer.className = 'procedure-report-page-scroll__header-spacer';
        const td = document.createElement('td');
        td.setAttribute('colspan', String(Math.max(colspan, 1)));
        td.style.padding = '0';
        td.style.border = '0';
        spacer.appendChild(td);
        tbody.insertBefore(spacer, tbody.firstChild);
      }

      const td = spacer.querySelector('td');
      spacer.style.height = `${headerHeight}px`;
      if (td) {
        td.style.height = `${headerHeight}px`;
        td.setAttribute('colspan', String(Math.max(colspan, 1)));
      }
    },
    removeStickyHeaderSpacer(tbody) {
      if (!tbody) return;

      const spacer = tbody.querySelector('tr.procedure-report-page-scroll__header-spacer');
      if (spacer) spacer.remove();
    },
    attachHorizontalScrollListener() {
      const wrapper = this.getScrollContainer();
      if (!wrapper || wrapper._procedureReportHScrollAttached) return;

      wrapper.addEventListener('scroll', this.onWrapperHorizontalScroll, { passive: true });
      wrapper._procedureReportHScrollAttached = true;
    },
    detachHorizontalScrollListener() {
      const wrapper = this.scrollContainer || this.getScrollContainer();
      if (!wrapper || !wrapper._procedureReportHScrollAttached) return;

      wrapper.removeEventListener('scroll', this.onWrapperHorizontalScroll);
      wrapper._procedureReportHScrollAttached = false;
    },
    attachTouchScrollListener() {
      const wrapper = this.getScrollContainer();
      if (!wrapper || wrapper._procedureReportTouchAttached) return;

      wrapper.addEventListener('touchstart', this.onWrapperTouchStart, { passive: true });
      wrapper.addEventListener('touchmove', this.onWrapperTouchMove, { passive: false });
      wrapper.addEventListener('touchend', this.onWrapperTouchEnd, { passive: true });
      wrapper.addEventListener('touchcancel', this.onWrapperTouchEnd, { passive: true });
      wrapper._procedureReportTouchAttached = true;
    },
    detachTouchScrollListener() {
      const wrapper = this.scrollContainer || this.getScrollContainer();
      if (!wrapper || !wrapper._procedureReportTouchAttached) return;

      wrapper.removeEventListener('touchstart', this.onWrapperTouchStart);
      wrapper.removeEventListener('touchmove', this.onWrapperTouchMove);
      wrapper.removeEventListener('touchend', this.onWrapperTouchEnd);
      wrapper.removeEventListener('touchcancel', this.onWrapperTouchEnd);
      wrapper._procedureReportTouchAttached = false;
      this.resetTouchScrollState();
    },
    resetTouchScrollState() {
      this.touchScroll.active = false;
      this.touchScroll.axis = null;
    },
    onWrapperTouchStart(event) {
      if (event.touches.length !== 1) {
        this.resetTouchScrollState();
        return;
      }

      const touch = event.touches[0];
      const wrapper = this.getScrollContainer();
      if (!wrapper) return;

      this.touchScroll.active = true;
      this.touchScroll.axis = null;
      this.touchScroll.startX = touch.clientX;
      this.touchScroll.startY = touch.clientY;
      this.touchScroll.startScrollLeft = wrapper.scrollLeft;
    },
    onWrapperTouchMove(event) {
      if (!this.touchScroll.active || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - this.touchScroll.startX;
      const deltaY = touch.clientY - this.touchScroll.startY;

      if (!this.touchScroll.axis) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        this.touchScroll.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }

      if (this.touchScroll.axis !== 'x') return;

      const wrapper = this.getScrollContainer();
      if (!wrapper) return;

      event.preventDefault();
      event.stopPropagation();

      const maxScrollLeft = Math.max(wrapper.scrollWidth - wrapper.clientWidth, 0);
      const nextScrollLeft = Math.min(
        Math.max(this.touchScroll.startScrollLeft - deltaX, 0),
        maxScrollLeft,
      );

      wrapper.scrollLeft = nextScrollLeft;
      this.syncFixedHeaderHorizontalScroll(nextScrollLeft);

      if (this.horizontalScroll.syncing) return;

      const mirror = this.$refs.horizontalScrollMirror;
      if (mirror) {
        this.horizontalScroll.syncing = true;
        mirror.scrollLeft = nextScrollLeft;
        window.requestAnimationFrame(() => {
          this.horizontalScroll.syncing = false;
        });
      }
    },
    onWrapperTouchEnd() {
      this.resetTouchScrollState();
    },
    updateScrollAnchors() {
      const layout = this.stickyLayoutEl
        || this.$el.closest('.procedure-report-table-layout')
        || this.$el;
      const wrapper = this.getScrollContainer();
      if (!layout || !wrapper) return;

      const layoutRect = layout.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      if (!this.isStickyHeaderContextVisible(layoutRect, wrapperRect)) {
        if (this.headerFixed) {
          this.resetStickyHeader();
        }
        return;
      }

      this.updateStickyHeader(layoutRect, wrapperRect);

      const nextLeft = Math.max(wrapperRect.left, 0);
      const nextWidth = wrapperRect.width;
      if (this.horizontalScroll.left !== nextLeft) {
        this.horizontalScroll.left = nextLeft;
      }
      if (this.horizontalScroll.width !== nextWidth) {
        this.horizontalScroll.width = nextWidth;
      }

      if (this.hasMore && this.loading) {
        this.updateAppendLoadingPosition();
      }
    },
    invalidateStickyHeaderCache() {
      this.stickyHeaderCache = null;
    },
    getStickyHeaderElements() {
      if (this.stickyHeaderCache) {
        const { wrapper, thead, tbody, headerRow } = this.stickyHeaderCache;
        if (wrapper.isConnected && thead.isConnected && tbody.isConnected && headerRow.isConnected) {
          return this.stickyHeaderCache;
        }
        this.stickyHeaderCache = null;
      }

      const wrapper = this.getScrollContainer();
      if (!wrapper) return null;

      const thead = wrapper.querySelector('thead.v-data-table-header');
      const tbody = wrapper.querySelector('tbody');
      const headerRow = thead ? thead.querySelector('tr') : null;
      if (!thead || !tbody || !headerRow) return null;

      this.stickyHeaderCache = { wrapper, thead, tbody, headerRow };
      return this.stickyHeaderCache;
    },
    updateStickyHeader(layoutRect, wrapperRect) {
      if (!this.isComponentVisible) {
        if (this.headerFixed) {
          this.resetStickyHeader();
        }
        return;
      }

      const elements = this.getStickyHeaderElements();
      if (!layoutRect || !wrapperRect || !elements) {
        this.resetStickyHeader();
        return;
      }

      if (!this.isStickyHeaderContextVisible(layoutRect, wrapperRect)) {
        if (this.headerFixed) {
          this.resetStickyHeader(elements);
        }
        return;
      }

      const { wrapper, thead, tbody, headerRow } = elements;
      const headerCells = Array.from(headerRow.querySelectorAll('th'));
      const headerHeight = this.stickyHeaderHeight || thead.offsetHeight;
      let shouldStick = false;

      if (this.headerFixed) {
        shouldStick = layoutRect.top <= 0 && layoutRect.bottom > headerHeight;
      } else {
        const headerTop = headerRow.getBoundingClientRect().top;
        shouldStick = headerTop <= 0 && layoutRect.bottom > headerHeight;
      }

      if (shouldStick) {
        const justStuck = !this.headerFixed;
        const reservedHeight = justStuck ? thead.offsetHeight : headerHeight;

        if (justStuck) {
          this.headerFixed = true;
          this.stickyHeaderHeight = reservedHeight;
          this.ensureStickyHeaderSpacer(tbody, reservedHeight, headerCells.length);
        }

        const scrollLeft = wrapper.scrollLeft;
        thead.classList.add('procedure-report-page-scroll__thead--fixed');
        thead.style.top = '0px';
        thead.style.left = `${wrapperRect.left}px`;
        thead.style.width = `${wrapperRect.width}px`;

        headerRow.style.width = `${wrapper.scrollWidth}px`;
        headerRow.style.minWidth = `${wrapper.scrollWidth}px`;
        headerRow.style.display = 'table';
        headerRow.style.tableLayout = 'fixed';
        headerRow.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;

        if (
          justStuck
          || !this.stickyHeaderColumnsSynced
          || this.stickyHeaderViewportWidth !== wrapperRect.width
        ) {
          this.stickyHeaderViewportWidth = wrapperRect.width;
          this.syncFixedHeaderColumnWidths();
        }

        this.headerStuck = true;
        return;
      }

      if (this.headerFixed) {
        this.resetStickyHeader(elements);
      }
    },
    isStickyHeaderContextVisible(layoutRect, wrapperRect) {
      return Boolean(
        layoutRect
        && wrapperRect
        && layoutRect.width > 0
        && layoutRect.height > 0
        && wrapperRect.width > 0,
      );
    },
    handleComponentVisibilityChange(isVisible) {
      if (!isVisible) {
        this.resetStickyHeader();
        this.invalidateStickyHeaderCache();
        return;
      }

      this.$nextTick(() => {
        this.resetStickyHeader();
        this.stickyLayoutEl = this.$el.closest('.procedure-report-table-layout') || this.$el;
        this.invalidateStickyHeaderCache();
        this.stickyHeaderStickScrollY = null;
        this.stickyHeaderColumnsSynced = false;
        this.measureStickyHeaderStickPoint();
        this.refreshScrollContainer();
        this.updateHorizontalScroll();
        this.updateScrollAnchors();
      });
    },
    connectVisibilityObserver() {
      if (this.visibilityObserver || typeof IntersectionObserver === 'undefined') return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
          if (isVisible === this.isComponentVisible) return;

          this.isComponentVisible = isVisible;
          this.handleComponentVisibilityChange(isVisible);
        },
        { threshold: [0, 0.01] },
      );
      this.visibilityObserver.observe(this.$el);
    },
    disconnectVisibilityObserver() {
      if (!this.visibilityObserver) return;

      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    },
    syncFixedHeaderColumnWidths() {
      const elements = this.getStickyHeaderElements();
      if (!elements) return;

      const { wrapper, headerRow } = elements;
      const headerCells = Array.from(headerRow.querySelectorAll('th'));
      const bodyRow = this.getStickyHeaderReferenceRow(wrapper);
      const cols = this.getColgroupCols();

      headerCells.forEach((th, index) => {
        const td = bodyRow ? bodyRow.querySelectorAll('td')[index] : null;
        const col = cols[index];
        let width = null;

        if (td) {
          width = td.offsetWidth;
        } else if (col) {
          width = col.offsetWidth || this.parseColumnSize(col.style.width);
        }

        if (!width) return;

        th.style.width = `${width}px`;
        th.style.minWidth = `${width}px`;
        th.style.maxWidth = `${width}px`;
        th.style.boxSizing = 'border-box';
      });
      this.stickyHeaderColumnsSynced = true;
    },
    clearFixedHeaderColumnWidths(headerRow) {
      if (!headerRow) return;

      Array.from(headerRow.querySelectorAll('th')).forEach((th) => {
        th.style.width = '';
        th.style.minWidth = '';
        th.style.maxWidth = '';
        th.style.boxSizing = '';
      });
    },
    resetStickyHeader(elements) {
      const els = elements || this.getStickyHeaderElements();
      if (!els) {
        this.headerFixed = false;
        this.headerStuck = false;
        return;
      }

      const { thead, tbody, headerRow } = els;
      thead.classList.remove('procedure-report-page-scroll__thead--fixed');
      thead.style.top = '';
      thead.style.left = '';
      thead.style.width = '';
      this.removeStickyHeaderSpacer(tbody);

      headerRow.style.width = '';
      headerRow.style.minWidth = '';
      headerRow.style.display = '';
      headerRow.style.tableLayout = '';
      headerRow.style.transform = '';
      this.clearFixedHeaderColumnWidths(headerRow);

      this.headerFixed = false;
      this.headerStuck = false;
      this.stickyHeaderColumnsSynced = false;
      this.stickyHeaderViewportWidth = 0;

      window.requestAnimationFrame(() => {
        if (!this.headerFixed) {
          this.measureStickyHeaderStickPoint();
        }
      });
    },
    syncFixedHeaderHorizontalScroll(scrollLeft) {
      if (!this.headerFixed) return;

      const elements = this.getStickyHeaderElements();
      if (!elements) return;

      const { wrapper, headerRow } = elements;
      const offset = scrollLeft !== undefined ? scrollLeft : wrapper.scrollLeft;
      headerRow.style.transform = `translate3d(-${offset}px, 0, 0)`;
      headerRow.style.width = `${wrapper.scrollWidth}px`;
      headerRow.style.minWidth = `${wrapper.scrollWidth}px`;
    },
    updateAppendLoadingPosition() {
      if (!this.hasMore || !this.loading) return;

      const sentinelRow = this.$el.querySelector('.infinite-scroll-table__sentinel-row');
      if (!sentinelRow) return;

      const rect = sentinelRow.getBoundingClientRect();
      const appendLoadingHeight = 44;
      this.appendLoadingTop = rect.top + (rect.height / 2) - (appendLoadingHeight / 2);
    },
    updateHorizontalScroll() {
      const wrapper = this.getScrollContainer();
      if (!wrapper) return;

      const contentWidth = wrapper.scrollWidth;
      const clientWidth = wrapper.clientWidth;
      const needsScroll = contentWidth > clientWidth + 1;

      this.horizontalScroll.contentWidth = contentWidth;
      this.horizontalScroll.visible = needsScroll;
      this.updateScrollAnchors();

      if (this.horizontalScroll.syncing) return;

      const mirror = this.$refs.horizontalScrollMirror;
      if (mirror) {
        mirror.scrollLeft = wrapper.scrollLeft;
      }
    },
    onWrapperHorizontalScroll() {
      const wrapper = this.getScrollContainer();
      if (wrapper) {
        this.syncFixedHeaderHorizontalScroll(wrapper.scrollLeft);
      }
      if (this.horizontalScroll.syncing) return;
      this.syncHorizontalScroll('wrapper');
    },
    syncHorizontalScroll(source) {
      const wrapper = this.getScrollContainer();
      const mirror = this.$refs.horizontalScrollMirror;
      if (!wrapper || !mirror) return;

      const scrollLeft = source === 'mirror' ? mirror.scrollLeft : wrapper.scrollLeft;

      this.horizontalScroll.syncing = true;

      if (source === 'mirror') {
        wrapper.scrollLeft = scrollLeft;
      } else {
        mirror.scrollLeft = scrollLeft;
      }

      this.syncFixedHeaderHorizontalScroll(scrollLeft);

      window.requestAnimationFrame(() => {
        this.horizontalScroll.syncing = false;
      });
    },
    connectHorizontalScrollObserver() {
      if (this.horizontalScrollResizeObserver || typeof ResizeObserver === 'undefined') return;

      const wrapper = this.getScrollContainer();
      if (!wrapper) return;

      this.horizontalScrollResizeObserver = new ResizeObserver(() => {
        this.updateHorizontalScroll();
        if (this.headerFixed) {
          this.stickyHeaderColumnsSynced = false;
          this.scheduleStickyHeaderSync();
        }
      });
      this.horizontalScrollResizeObserver.observe(wrapper);

      const table = wrapper.querySelector('table');
      if (table) {
        this.horizontalScrollResizeObserver.observe(table);
      }
    },
    disconnectHorizontalScrollObserver() {
      if (!this.horizontalScrollResizeObserver) return;

      this.horizontalScrollResizeObserver.disconnect();
      this.horizontalScrollResizeObserver = null;
    },
    connectObserver() {
      if (this.observer || !this.hasMore) return;
      if (!this.$refs.sentinel) return;

      this.observer = new IntersectionObserver(
        this.handleIntersect,
        {
          root: null,
          threshold: this.threshold,
          rootMargin: this.rootMargin,
        },
      );

      this.observer.observe(this.$refs.sentinel);
    },
    reconnectObserver() {
      this.disconnectObserver();
      this.connectObserver();
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    getItemsLength() {
      return Array.isArray(this.items) ? this.items.length : 0;
    },
    resumeLoadMore() {
      this.loadMoreSuspended = false;
    },
    handleIntersect(entries) {
      const [entry] = entries;
      if (!entry || !entry.isIntersecting) return;
      this.requestMore();
    },
    requestMoreIfNearEnd() {
      const scrollTop = window.pageYOffset
        || document.documentElement.scrollTop
        || document.body.scrollTop
        || 0;
      const clientHeight = window.innerHeight
        || document.documentElement.clientHeight
        || 0;
      const scrollHeight = document.documentElement.scrollHeight
        || document.body.scrollHeight
        || 0;

      const scrollable = scrollHeight - clientHeight;
      if (scrollable <= 0) {
        if (this.autoLoadOnMount) this.requestMore();
        return;
      }

      const progress = scrollTop / scrollable;
      if (progress >= this.triggerPercent) {
        this.requestMore();
      }
    },
    requestMore() {
      if (!this.hasMore || this.loading || this.loadMoreSuspended) return;
      this.loadMoreRequestPending = true;
      this.$emit('load-more');
    },
    toggleSort(header) {
      if (this.isSelectionHeader(header)) return;
      if (header.sortable === false) return;

      let sortBy = [...this.localSortBy];
      let sortDesc = [...this.localSortDesc];

      const index = sortBy.indexOf(header.value);
      if (index === -1) {
        sortBy = [header.value];
        sortDesc = [false];
      } else if (sortDesc[index] === false) {
        sortDesc[index] = true;
      } else {
        sortBy.splice(index, 1);
        sortDesc.splice(index, 1);
      }

      this.localSortBy = sortBy;
      this.localSortDesc = sortDesc;
      this.resumeLoadMore();

      const nextOptions = {
        ...(this.$attrs.options || {}),
        sortBy: [...this.localSortBy],
        sortDesc: [...this.localSortDesc],
      };

      this.$emit('update:options', nextOptions);
      this.$emit('update:sort-by', [...this.localSortBy]);
      this.$emit('update:sort-desc', [...this.localSortDesc]);
    },
    toggleSelectAll(on, value) {
      if (on && typeof on['toggle-select-all'] === 'function') {
        on['toggle-select-all'](value);
      }
    },
    getSortIcon(header) {
      const index = this.localSortBy.indexOf(header.value);

      if (index === -1) return 'mdi-swap-vertical';
      return this.localSortDesc[index] ? 'mdi-arrow-down' : 'mdi-arrow-up';
    },
    getAriaSort(header) {
      const index = this.localSortBy.indexOf(header.value);

      if (index === -1) return 'none';
      return this.localSortDesc[index] ? 'descending' : 'ascending';
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshScrollContainer();
      if (this.enableColumnResize) {
        this.syncColumnWidths();
      }
      this.connectHorizontalScrollObserver();
      this.connectVisibilityObserver();
      this.measureStickyHeaderStickPoint();
      this.updateHorizontalScroll();
      this.updateScrollAnchors();
    });

    if (this.autoLoadOnMount && this.hasMore && !this.loading) {
      this.$emit('load-more');
    }
  },
  beforeDestroy() {
    this.disconnectObserver();
    this.detachScrollListener();
    this.detachHorizontalScrollListener();
    this.detachTouchScrollListener();
    this.disconnectHorizontalScrollObserver();
    this.disconnectVisibilityObserver();
    if (this.stickyHeaderSyncRaf) {
      window.cancelAnimationFrame(this.stickyHeaderSyncRaf);
      this.stickyHeaderSyncRaf = null;
    }
    this.resetStickyHeader();
    this.cleanupResizeEvents();
  },
  updated() {
    if (!this.headerFixed || !this.isComponentVisible) return;
    this.stickyHeaderColumnsSynced = false;
    this.scheduleStickyHeaderSync();
  },
};
</script>

<style scoped>
.procedure-report-page-scroll {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  position: relative;
  width: 100%;
}

.procedure-report-page-scroll--sticky-header ::v-deep .v-data-table__wrapper table thead.v-data-table-header {
  background-color: var(--v-muted-foreground-base) !important;
}

.procedure-report-page-scroll--sticky-header ::v-deep .v-data-table__wrapper table thead th {
  background: transparent !important;
}

.procedure-report-page-scroll--sticky-header ::v-deep .v-data-table__wrapper table thead.procedure-report-page-scroll__thead--fixed {
  background-color: var(--v-muted-foreground-base) !important;
  box-shadow: 0 1px 0 color-mix(in srgb, var(--v-shadow-base) 18%, transparent);
  display: block;
  overflow: hidden;
  position: fixed;
  z-index: 4;
}

.procedure-report-page-scroll--sticky-header ::v-deep .v-data-table__wrapper table thead.procedure-report-page-scroll__thead--fixed th {
  background: transparent !important;
}

.procedure-report-page-scroll--sticky-header ::v-deep .v-data-table__wrapper table thead.procedure-report-page-scroll__thead--fixed tr {
  border-collapse: collapse;
  will-change: transform;
}

::v-deep tr.procedure-report-page-scroll__header-spacer td {
  border: 0 !important;
  line-height: 0;
  padding: 0 !important;
  pointer-events: none;
  vertical-align: top;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
  overflow-y: visible;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  touch-action: pan-y pinch-zoom;

  &::-webkit-scrollbar {
    display: none;
    height: 0;
  }
}

.procedure-report-page-scroll__hscroll-fixed {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x pinch-zoom;
  background: color-mix(in srgb, var(--v-background-base) 96%, transparent);
  bottom: 0;
  box-shadow: 0 -1px 0 color-mix(in srgb, var(--v-shadow-base) 14%, transparent);
  height: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  position: fixed;
  z-index: 8;
}

.procedure-report-page-scroll__hscroll-spacer {
  height: 1px;
  pointer-events: none;
}

::v-deep .v-data-table__wrapper table tbody tr:last-child td,
::v-deep .v-data-table__wrapper table tbody > tr.v-data-table__empty-wrapper > td {
  border-bottom: none !important;
}

.infinite-scroll-table--resizable ::v-deep .v-data-table__wrapper table {
  table-layout: fixed;
}

.infinite-scroll-header-cell--resizable {
  position: relative;
}

.infinite-scroll-header-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
}

.infinite-scroll-header-resizer::after {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 2px;
  width: 1px;
  background-color: color-mix(in srgb, var(--v-shadow-base) 20%, transparent);
}

.infinite-scroll-header-cell--resizable:hover .infinite-scroll-header-resizer::after {
  background-color: var(--v-primary-base);
}

.infinite-scroll-table__sentinel-row td {
  border: 0;
  height: 44px;
  padding: 10px 0;
  vertical-align: middle;
}

.infinite-scroll-table__sentinel-anchor {
  height: 1px;
  margin: 0 auto;
  pointer-events: none;
  width: 1px;
}

.procedure-report-page-scroll__append-loading {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  height: 44px;
  justify-content: center;
  padding: 10px 0;
  pointer-events: none;
  position: fixed;
  z-index: 7;
}

.infinite-scroll-header-cell {
  gap: 4px;
}

.infinite-scroll-header-text {
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
